import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useTranslation } from 'src/i18n';
import CountVerdictTable from '../Commissioning/CountVerdictTable';
import NotesField from '../Commissioning/NotesField';

// Sections 5 and 6: EHT circuit field audit and insulation field audit. Grouped in one tab
// because both are count grids of the same shape, sharing one recommendations block.
const CIRCUIT_ROWS = [
  { key: 'visualDeficiencies', hasCount: false },
  { key: 'investigatedCircuits', hasCount: true, questionKey: 'matchAsBuilt' },
  { key: 'openCircuits', hasCount: true, questionKey: 'reinstatedInField' },
  { key: 'shortCircuits', hasCount: true, questionKey: 'reinstatedInField' },
  { key: 'failedIrTests', hasCount: true, hasVerdict: false },
  { key: 'failedOhmTests', hasCount: true, hasVerdict: false },
  { key: 'operationalTestFailures', hasCount: true, hasVerdict: false },
];

const INSULATION_ROWS = [
  { key: 'investigatedCircuits', hasCount: true, questionKey: 'matchAsBuilt' },
  { key: 'code1', hasCount: true, hasVerdict: false },
  { key: 'code2', hasCount: true, hasVerdict: false },
  { key: 'code3', hasCount: true, hasVerdict: false },
  { key: 'code4', hasCount: true, hasVerdict: false },
  { key: 'other', hasCount: true, hasVerdict: false },
];

export default function HmsAuditCircuitInsulation (props) {
  const { data, onChange, readOnly } = props;
  const { t } = useTranslation();
  const circuit = data?.get('circuitAudit');
  const insulation = data?.get('insulationAudit');

  const buildRows = (rows, section) => rows.map(row => ({
    ...row,
    label: t(`hmsAudit.${section}.${row.key}`),
    question: row.questionKey ? t(`hmsAudit.${row.questionKey}`) : '',
  }));

  const circuitRows = useMemo(() => buildRows(CIRCUIT_ROWS, 'circuit'), [t]);
  const insulationRows = useMemo(() => buildRows(INSULATION_ROWS, 'insulation'), [t]);

  return (
    <div>
      <CountVerdictTable
        title={t('hmsAudit.circuitAuditSummary')}
        labelHeader={t('hmsAudit.table.circuitCounts')}
        rows={circuitRows}
        data={circuit}
        extrasPath={['extras', 'circuitAudit']}
        onChange={onChange}
        readOnly={readOnly}
        footnote={t('hmsAudit.circuit.operationalTestNote')}
      />

      <CountVerdictTable
        title={t('hmsAudit.insulationAuditSummary')}
        labelHeader={t('hmsAudit.table.circuitCounts')}
        rows={insulationRows}
        data={insulation}
        extrasPath={['extras', 'insulationAudit']}
        onChange={onChange}
        readOnly={readOnly}
      />

      {/* One recommendations block covers both sections. */}
      <NotesField
        title={t('hmsAudit.recommendations')}
        value={circuit?.get('recommendations')}
        path={['extras', 'circuitAudit', 'recommendations']}
        onChange={onChange}
        readOnly={readOnly}
      />
    </div>
  );
}

HmsAuditCircuitInsulation.propTypes = {
  data: ImmutablePropTypes.map,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

HmsAuditCircuitInsulation.defaultProps = {
  readOnly: false,
};
